import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaList
} from 'react-icons/fa';
import { getAffiliates } from '../../services/companyAuthService';

const AddAffiliates = () => {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all'
  });

  useEffect(() => {
    fetchAffiliates();
  }, [filters]);

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const data = await getAffiliates({
        status: filters.status !== 'all' ? filters.status : undefined,
        type: filters.type !== 'all' ? filters.type : undefined,
        search: searchTerm || undefined
      });

      // Filter affiliates that have userId
      const affiliatesWithUserId = data.filter(affiliate => affiliate.userId);
      setAffiliates(affiliatesWithUserId);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      toast.error(error.message || 'Failed to fetch affiliates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/company/dashboard/affiliates/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/company/dashboard/affiliates/view/${id}`);
  };

  const handleViewCampaigns = (email) => {
    navigate(`/company/dashboard/affiliates/affiliate-campaigns/${encodeURIComponent(email)}`);
  };

  const handleAddCampaign = (userId) => {
    navigate(`/company/dashboard/affiliates/add-campaigns/${userId}`);
  };



  const filteredAffiliates = affiliates.filter(affiliate => {
    const matchesSearch =
      affiliate.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (affiliate.companyName && affiliate.companyName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filters.status === 'all' || affiliate.status === filters.status;
    const matchesType = filters.type === 'all' || affiliate.affiliateType === filters.type;

    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="flex flex-col lg:flex-row min-h-full bg-gray-100">
      <div className="flex-1 p-0 md:p-4 lg:p-6">
        <div className="bg-white shadow-md rounded-lg p-4 lg:p-6 mb-10 lg:mb-0">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg md:text-xl font-bold">Active Affiliates</h2>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search affiliates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>

            <div className="flex flex-wrap gap-4">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-4 py-2 sm:px-4 sm:py-2 px-2 py-1 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>

              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="px-4 py-2 sm:px-4 sm:py-2 px-2 py-1 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Types</option>
                <option value="individual">Individual</option>
                <option value="influencer">Influencer</option>
                <option value="company">Company</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Instagram ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaigns
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Add Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredAffiliates.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No affiliates found
                    </td>
                  </tr>
                ) : (
                  filteredAffiliates.map((affiliate) => (
                    <tr key={`${affiliate.email}-${affiliate.instagramId || ''}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {affiliate.firstName && affiliate.lastName 
                                ? `${affiliate.firstName} ${affiliate.lastName}`
                                : affiliate.username || 'Unnamed Affiliate'}
                            </div>
                            {affiliate.companyName && (
                              <div className="text-sm text-gray-500">
                                {affiliate.companyName}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {affiliate.affiliateType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {affiliate.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {affiliate.instagramId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewCampaigns(affiliate.email)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 flex items-center gap-1"
                          title="View Campaigns"
                        >
                          <FaList />
                          <span className="text-xs">View ({affiliate.campaigns?.length || 0})</span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {affiliate.userId ? (
                          <button
                            onClick={() => handleAddCampaign(affiliate.userId)}
                            className="text-orange-500 hover:text-orange-600 p-1 rounded hover:bg-orange-100 flex items-center gap-1"
                            title="Add Campaign"
                          >
                            <FaPlus />
                            <span className="text-xs">Add</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs italic">Not registered</span>
                        )}
                        {/* <button
                          onClick={() => handleAddCampaign(affiliate.userId)}
                          className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 flex items-center gap-1"
                          title="Add Campaign"
                        >
                          <FaPlus />
                          <span className="text-xs">Add</span>
                        </button> */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleView(affiliate._id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handleEdit(affiliate._id)}
                            className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAffiliates;
